import React, { FC, ReactNode } from "react";
import { User } from "./User";

import { getDuration } from "@helpers/getDuration";
import { LanguageLevel, TourByIdQuery } from "@graphql/types";
import { Trans } from "@helpers/translate";
import { useRouter } from "next/router";
import Icon, { IconType } from "@components/ui/Icon";
import { Divider } from "@components/ui/Divider";
import { ShowMore } from "@components/ui/ShowMore";
import { Paragraph } from "@components/ui/Paragraph";
import useIsMobile from "@helpers/useIsMobile";

interface ItemProps {
  icon: IconType;
  children?: ReactNode;
}

const Item: FC<ItemProps> = ({ icon, children }) => {
  return (
    <div className="flex items-center mb-3">
      <div className="mr-2 w-6 h-6 flex items-center">
        <Icon icon={icon} size="sm" />
      </div>
      <div className="truncate">{children}</div>
    </div>
  );
};

interface Props {
  tour: NonNullable<TourByIdQuery["tourById"]>;
}

export const Header: FC<Props> = ({ tour }) => {
  const router = useRouter();
  const supplier = tour.supplier;
  const maxPeople = tour.people.max;
  const tourCityName = tour.city.name;
  const isMobile = useIsMobile();

  return (
    <div>
      <h1 className="text-3xl mb-2 w-full mt-2">{tour.title}</h1>
      {tour.category && (
        <div
          className={`bg-yellow-400 text-gray-600 inline-block text-center no-underline whitespace-no-wrap pointer rounded px-3 py-1 text-xs uppercase`}
        >
          {tour.category?.name}
        </div>
      )}
      <Divider className="my-4" />

      <User
        displayName={supplier.displayName}
        rating={supplier.stats.rating}
        reviewsCount={supplier.stats.reviewsCount}
        picture={isMobile ? supplier?.picture?.urlXS : supplier.picture?.url}
        cityName={tourCityName}
        createdAt={supplier.createdAt}
        url={supplier.url}
        onReviewClicked={() => {
          router.push("#reviews");
        }}
        onUserClicked={() => {
          router.push(supplier.url);
        }}
        showVerifiedData
      />

      <Divider className="my-4" />

      <div className="flex items-center flex-wrap">
        <div className="w-full md:w-1/3">
          <Item icon="marker">{tour.city.name}</Item>
        </div>

        <div className="w-full md:w-2/3">
          <Item icon="chat">
            {supplier.languages
              .map((item) =>
                item.level === LanguageLevel.Native
                  ? `${item.language.name} (Native)`
                  : item.language.name
              )
              .join(", ")}
          </Item>
        </div>
        <div className="w-full md:w-1/3">
          <Item icon="time">{getDuration(tour.duration, "short")}</Item>
        </div>
        <div className="w-full md:w-2/3">
          <Item icon="lock">
            <Trans id="tour.feature.private">
              Private tour (only you and {supplier?.displayName})
            </Trans>
          </Item>
        </div>
        <div className="w-full md:w-1/3">
          <Item icon="user">
            <Trans id="tour.feature.people">Up to {maxPeople} people</Trans>
          </Item>
        </div>
      </div>

      <ShowMore className="mt-4 mb-8">
        <Paragraph>{tour.overview || tour.content}</Paragraph>
      </ShowMore>
    </div>
  );
};
